import type { DiagnosticsCheck } from './types'

/**
 * What the diagnostics panel's footer says about its button.
 *
 * Here rather than in `diagnostics-panel.tsx` because the skeleton renders it
 * for real (P-05 §1) and that panel is `lazy()` — importing the string from
 * there would drag the panel's chunk into the eager bundle. Same reason
 * `log-source-columns` had for existing, and the same trap.
 */
export const RUN_EXPLANATION =
	'Runs on the collector — results can take up to 60 seconds. Expand a check for raw command output.'

/**
 * The check whose warning has a known cause and a known next step.
 *
 * Named by id rather than by position: the set of checks is backend truth we do
 * not have yet (`decisions.md`), so the list may grow, shrink or reorder — but
 * an id that stops existing simply falls through to the generic sentence below
 * rather than mis-labelling whichever check happened to be second.
 */
const TRAFFIC_CAPTURE_CHECK_ID = 'traffic-capture'

/**
 * What a warning on the traffic-capture check actually means.
 *
 * Worth its own sentence because the check's own result line (`No packets
 * during 30s capture`) reads as a fault on our side, and it usually is not: the
 * listener is up, so the next place to look is the device's syslog config.
 */
const NO_TRAFFIC_EXPLANATION =
	'Port is listening but nothing arrived during the capture — likely a quiet source or a sender misconfiguration. Check the device’s syslog settings.'

/**
 * One sentence above the checks, or `null` when every check passed.
 *
 * `null` rather than an all-clear banner: four green ticks already say it, and a
 * band repeating them is the kind of reassurance that trains people to skip the
 * region where a real warning would appear.
 */
export function summariseDiagnostics(
	checks: DiagnosticsCheck[],
): string | null {
	const warnings = checks.filter((check) => check.status === 'warn')
	if (warnings.length === 0) return null

	if (warnings.some((check) => check.id === TRAFFIC_CAPTURE_CHECK_ID)) {
		return NO_TRAFFIC_EXPLANATION
	}

	// Names, not results: the result line is already rendered on the check's own
	// row a few pixels below, and repeating it verbatim would make the band look
	// like a second, different finding.
	return `${warnings.map((check) => check.name).join(' · ')} — see the result below.`
}
